import React, { useState, useRef, useEffect } from 'react';
import api from '../core/api';

interface Message {
    role: 'user' | 'assistant';
    content: string;
    ts: number;
}

const SESSION_KEY = 'diana-support-session';
const MESSAGES_KEY = 'diana-support-messages';

const getSessionId = () => {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
        id = `support_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
};

const SUGGESTIONS = [
    'Mi WhatsApp se desconectó',
    'Diana no responde mensajes',
    'Cómo agrego un servicio',
    'Cómo configuro el horario',
];

const SupportChat: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>(() => {
        try {
            return JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]');
        } catch { return []; }
    });
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [showBadge, setShowBadge] = useState(true);
    const bottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages.slice(-30)));
    }, [messages]);

    useEffect(() => {
        if (open) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            setTimeout(() => inputRef.current?.focus(), 100);
            setShowBadge(false);
        }
    }, [open, messages]);

    const send = async (text?: string) => {
        const msg = (text || input).trim();
        if (!msg || loading) return;

        setInput('');
        const userMsg: Message = { role: 'user', content: msg, ts: Date.now() };
        setMessages(prev => [...prev, userMsg]);
        setLoading(true);

        try {
            const { data } = await api.post('/support/chat', {
                message: msg,
                sessionId: getSessionId(),
            });
            setMessages(prev => [...prev, { role: 'assistant', content: data.reply, ts: Date.now() }]);
        } catch {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Tuve un problema al procesar tu mensaje. Intenta de nuevo o escríbenos por WhatsApp.',
                ts: Date.now()
            }]);
        } finally {
            setLoading(false);
        }
    };

    const clearChat = () => {
        setMessages([]);
        sessionStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(MESSAGES_KEY);
    };

    const hasUnread = !open && messages.length > 0 && messages[messages.length - 1].role === 'assistant';

    return (
        <>
            {/* Burbuja */}
            <button
                onClick={() => setOpen(v => !v)}
                className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg flex items-center justify-center transition-all active:scale-95"
                aria-label="Soporte Diana"
            >
                {open
                    ? <span className="material-symbols-outlined text-white text-[24px]">close</span>
                    : <span className="material-symbols-outlined text-white text-[24px]">smart_toy</span>
                }
                {(showBadge || hasUnread) && !open && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-accent/60 rounded-full border-2 border-white animate-pulse" />
                )}
            </button>

            {/* Ventana de chat */}
            {open && (
                <div className="fixed bottom-40 right-4 z-40 w-[calc(100vw-32px)] max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden"
                    style={{ height: '480px' }}>

                    {/* Header */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-primary text-white flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="material-symbols-outlined text-[18px]">smart_toy</span>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-bold leading-none">Diana IA · Soporte</p>
                            <p className="text-[11px] text-primary mt-0.5">Responde al instante · 24/7</p>
                        </div>
                        {messages.length > 0 && (
                            <button onClick={clearChat} title="Limpiar chat"
                                className="text-primary hover:text-white transition-colors">
                                <span className="material-symbols-outlined text-[18px]">delete_sweep</span>
                            </button>
                        )}
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                        {messages.length === 0 && (
                            <div className="space-y-3">
                                <div className="flex gap-2">
                                    <div className="w-6 h-6 rounded-full bg-primary/12 dark:bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="material-symbols-outlined text-primary text-[13px]">smart_toy</span>
                                    </div>
                                    <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-tl-none px-3 py-2 max-w-[85%]">
                                        <p className="text-sm text-slate-700 dark:text-slate-200">
                                            ¡Hola! 👋 Soy Diana, tu asistente de soporte. ¿En qué puedo ayudarte hoy?
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 ml-8">
                                    {SUGGESTIONS.map(s => (
                                        <button key={s} onClick={() => send(s)}
                                            className="text-xs px-3 py-1.5 rounded-full border border-primary/20 dark:border-primary/30 text-primary dark:text-primary hover:bg-primary/8 dark:hover:bg-primary/10 transition-colors text-left">
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {messages.map((msg, i) => (
                            <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                {msg.role === 'assistant' && (
                                    <div className="w-6 h-6 rounded-full bg-primary/12 dark:bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <span className="material-symbols-outlined text-primary text-[13px]">smart_toy</span>
                                    </div>
                                )}
                                <div className={`px-3 py-2 rounded-2xl max-w-[85%] text-sm whitespace-pre-wrap ${
                                    msg.role === 'user'
                                        ? 'bg-primary text-white rounded-tr-none'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-tl-none'
                                }`}>
                                    {msg.content}
                                </div>
                            </div>
                        ))}

                        {loading && (
                            <div className="flex gap-2">
                                <div className="w-6 h-6 rounded-full bg-primary/12 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
                                    <span className="material-symbols-outlined text-primary text-[13px]">smart_toy</span>
                                </div>
                                <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-tl-none px-4 py-2.5">
                                    <div className="flex gap-1">
                                        {[0, 1, 2].map(i => (
                                            <div key={i} className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
                                                style={{ animationDelay: `${i * 0.15}s` }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input */}
                    <div className="px-3 py-3 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
                        <div className="flex gap-2 items-center bg-slate-50 dark:bg-slate-800 rounded-xl px-3 py-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                                placeholder="Escribe tu duda..."
                                className="flex-1 bg-transparent text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 outline-none"
                            />
                            <button
                                onClick={() => send()}
                                disabled={!input.trim() || loading}
                                className="w-8 h-8 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 flex items-center justify-center transition-colors flex-shrink-0"
                            >
                                <span className="material-symbols-outlined text-white text-[16px]">send</span>
                            </button>
                        </div>
                        <p className="text-[10px] text-slate-400 text-center mt-1.5">Powered by Diana IA</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default SupportChat;
